import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { isSet, layoutRulesValidation } from '../../../Utils/CommonFunctions';
import { connect } from 'react-redux';
import {
    clearSubformValidation,
    setSubFormDataAction,
} from '../../../store/actions/formBuilderActions';
import { getSectionData, getColumnCount, processFieldData, createNewSubformRow } from './utils/subformHelpers';
import {
    createSelectDataHandler,
    createSelectAllHandler,
    createRemoveSubFormsHandler,
    createCancelSubFormsHandler,
    createEditSubFormsHandler,
    createSaveSubFormsHandler,
    createCopySubFormsHandler,
    createAddRowHandler
} from './utils/subformHandlers';

const ScmSubFormBody = (props) => {
    const {
        subformIndex,
        sectionSubformData,
        pageType,
        modType,
        isWrite,
        section,
        reminderDisplayText,
        shouldRenderButton,
        children,
        elmtParams,
        subformData,
        selectHandler,
    } = props;
    
    const { convertAndRenderElement, dynamicVar,attributes,defProps } = elmtParams || {};
    const subformHandlers = dynamicVar?._subformHandlers || {};
    
    // Get setSubFormDataAction from Redux connect (matching SubFormRows pattern)
    // setSubFormDataAction comes from mapDispatchToProps via connect
    const setSubFormDataAction = props.setSubFormDataAction;
    const clearSubformValidation = props.clearSubformValidation || subformHandlers.clearSubformValidation;
    const onChangeInputAction = subformHandlers.onChangeInputAction || props.onChangeInputAction;
    
    const subFormIndex = subformIndex > 0 ? subformIndex : 0;
    
    // Get layout rules related props from defProps
    const layoutRules = defProps?.layoutRules || {};
    const rulesMapping = defProps?.rulesMapping || {};
    const layoutObject = defProps?.layoutObject || {};
    const fieldGroups = defProps?.fieldGroups || [];
    const validationForms = defProps?.validationForms || {};
    
    // Memoize section data with layout rules evaluation
    const sectionData = useMemo(() => {
        let processedSectionData = getSectionData(section, modType);
        
        // Evaluate layout rules if available
        if (layoutRules && Object.keys(layoutRules).length > 0 && fieldGroups && fieldGroups.length > 0) {
            // Create formData object for layout rules validation
            const formData = {
                fieldGroups: fieldGroups,
                subformData: subformData || [],
                actionButton: defProps?.actionButton || {}
            };
            
            // Evaluate layout rules
            const { updateFieldGroups } = layoutRulesValidation(
                layoutRules,
                formData,
                rulesMapping,
                layoutObject,
                validationForms
            );
            
            // Find the section index in fieldGroups
            const sectionIndex = updateFieldGroups.findIndex((s) => s.id === section.id);
            if (sectionIndex !== -1 && updateFieldGroups[sectionIndex].data) {
                // Update section data with layout rules evaluation results
                processedSectionData = updateFieldGroups[sectionIndex].data.map((field) => {
                    const originalField = processedSectionData.find((f) => f.id === field.id);
                    if (originalField) {
                        return {
                            ...originalField,
                            access_mode: field.access_mode || originalField.access_mode
                        };
                    }
                    return field;
                });
            }
        }
        
        // Filter out hidden fields
        return processedSectionData.filter((item) => 
            isSet(item.access_mode, 'EDITABLE') !== 'HIDDEN'
        );
    }, [section, modType, layoutRules, rulesMapping, layoutObject, fieldGroups, subformData, validationForms]);
    
    // Memoize column count
    const colCount = useMemo(() => {
        return getColumnCount(sectionData);
    }, [sectionData]);
    
    // Memoize select data handler
    const selectDataHandler = useCallback((ind, val) => {
        const handler = createSelectDataHandler({
            subformData,
            subformIndex: subFormIndex,
            modType,
            setSubFormDataAction
        });
        return handler(ind, val);
    }, [subformData, subFormIndex, modType, setSubFormDataAction]);
    
    // Use prop selectHandler if provided, otherwise use the local implementation
    const finalSelectHandler = selectHandler || props.selectHandler || selectDataHandler;
    
    // Memoize select all handler
    const selectAllHandler = useCallback((val) => {
        const handler = createSelectAllHandler({
            subformData,
            subformIndex: subFormIndex,
            modType,
            setSubFormDataAction
        });
        return handler(val);
    }, [subformData, subFormIndex, modType, setSubFormDataAction]);
    
    // Factory function to create row-level handlers (bound to specific row)
    const createRowHandlers = useCallback((rowIndex) => {
        return {
            removeHandler: () => {
                const handler = createRemoveSubFormsHandler({
                    subformData,
                    subformIndex: subFormIndex,
                    rowIndex,
                    pageType,
                    modType,
                    setSubFormDataAction,
                    clearSubformValidation,
                    removeSubformDataHandler: props.removeSubformDataHandler
                });
                return handler();
            },
            cancelHandler: () => {
                const handler = createCancelSubFormsHandler({
                    subformData,
                    subformIndex: subFormIndex,
                    rowIndex,
                    modType,
                    setSubFormDataAction
                });
                return handler();
            },
            editHandler: () => {
                const handler = createEditSubFormsHandler({
                    subformData,
                    subformIndex: subFormIndex,
                    rowIndex,
                    modType,
                    setSubFormDataAction
                });
                return handler();
            },
            saveHandler: () => {
                const handler = createSaveSubFormsHandler({
                    subformData,
                    subformIndex: subFormIndex,
                    rowIndex,
                    saveAndUpdateSubform: props.saveAndUpdateSubform
                });
                return handler();
            },
            copyHandler: () => {
                const handler = createCopySubFormsHandler({
                    subformData,
                    subformIndex: subFormIndex,
                    rowIndex,
                    modType,
                    setSubFormDataAction
                });
                return handler();
            }
        };
    }, [subformData, subFormIndex, pageType, modType, setSubFormDataAction, clearSubformValidation, props.removeSubformDataHandler, props.saveAndUpdateSubform]);
    
    // Ref to track if we've already triggered auto-add to prevent duplicate calls
    // Declared early so it can be used in handlers
    const hasAutoAddedRef = useRef(false);
    
    // ADD SUBFORM ROW HANDLER (memoized)
    const addSubformRowHandler = useCallback(() => {
        const handler = createAddRowHandler({
            section,
            subformData,
            subformIndex: subFormIndex,
            modType,
            setSubFormDataAction
        });
        return handler();
    }, [section, subformData, subFormIndex, modType, setSubFormDataAction]);
    
    // BULK DELETE HANDLER - matching pattern from index.js
    const bulkDeleteHandler = async () => {
        if (subformData && subformData[subFormIndex]) {
            const selecteddata = currentSectionSubformData.subform_rows.filter((item) => item.is_selected === true);
            
            if (currentSectionSubformData.subform_rows.length === selecteddata.length) {
                // If all rows are selected, clear all and add a new empty row
                selecteddata.forEach((val, rowIndex) => {
                    if (clearSubformValidation) {
                        clearSubformValidation(modType, rowIndex);
                    }
                });
                const updatedSubformData = [...subformData];
                updatedSubformData[subFormIndex] = {
                    ...updatedSubformData[subFormIndex],
                    subform_rows: []
                };
                if (setSubFormDataAction) {
                    await setSubFormDataAction(modType, updatedSubformData);
                }
                // Add new empty row after deletion
                // Set ref to prevent useEffect from also adding a row
                if (addSubformRowHandler) {
                    hasAutoAddedRef.current = true;
                    addSubformRowHandler();
                    // Reset ref after a short delay
                    setTimeout(() => {
                        hasAutoAddedRef.current = false;
                    }, 100);
                }
            } else if (selecteddata.length > 0) {
                // Delete only selected rows
                const updateddata = currentSectionSubformData.subform_rows.filter((item) => item.is_selected === false);
                updateddata.forEach((val, rowIndex) => {
                    if (clearSubformValidation) {
                        clearSubformValidation(modType, rowIndex);
                    }
                });
                const updatedSubformData = [...subformData];
                updatedSubformData[subFormIndex] = {
                    ...updatedSubformData[subFormIndex],
                    subform_rows: updateddata
                };
                if (setSubFormDataAction) {
                    setSubFormDataAction(modType, updatedSubformData);
                }
            }
        }
    };
    
    // BULK EDIT HANDLER - opens bulk edit popup/modal
    // This handler can be extended to show a popup for bulk editing
    const bulkEditHandler = () => {
        // Check if multiple rows are selected
        const selectedRows = currentSectionSubformData.subform_rows.filter((item) => item.is_selected === true);
        if (selectedRows.length > 1) {
            // This can trigger a popup/modal for bulk editing
            // For now, we'll just provide the handler - the popup can be implemented in custom markup
            // You can access selectedRows and section.data to build the bulk edit form
            console.log('Bulk Edit - Selected rows:', selectedRows.length);
            // Return data that can be used by custom markup to show popup
            return {
                selectedRows: selectedRows,
                sectionData: sectionData,
                canEdit: true
            };
        }
        return null;
    };
    
    // Helper function to process field data - now uses utility function
    const processFieldDataWrapper = useCallback((item, rowData, rowIndex, rowId) => {
        const processed = processFieldData(item, rowData, rowIndex, rowId);
        // Ensure section_index and modType are set correctly
        if (processed.fieldObj) {
            processed.fieldObj.section_index = section.section_index;
            processed.fieldObj.modType = modType;
        }
        if (processed.updatedItem) {
            processed.updatedItem.modType = modType;
        }
        return processed;
    }, [section, modType]);
    
    // Recalculate sectionSubformData from subformData prop to ensure it's up-to-date
    // This ensures the component re-renders with latest data after Redux state updates
    const currentSectionSubformData = (subformIndex >= 0 && subformData && subformData[subformIndex]) 
        ? subformData[subformIndex] 
        : sectionSubformData;
    
    // Auto-add empty row when subform doesn't exist (matching SubformTypeForm behavior)
    // Only adds row when subform is first created (indexing === -1), not when it exists but is empty
    useEffect(() => {
        // Check if subform exists in subformData array (matching SubformTypeForm's indexing check)
        const indexing = isSet(subformData, []).findIndex((i) => i.field_group_section_id === section.id);
        
        // Only create subform with empty row if it doesn't exist yet (indexing === -1)
        // This matches SubformTypeForm behavior - it doesn't add rows to existing empty subforms
        if (indexing === -1 && 
            modType !== "DETAILVIEW" && 
            pageType === "form" && 
            setSubFormDataAction &&
            !hasAutoAddedRef.current) {
            
            // Create new row using createNewSubformRow helper (only if not DETAILVIEW)
            // Match SubformTypeForm: use section.subform_rows if available, otherwise create new empty row
            const subformRowsArr = modType === "DETAILVIEW" ? [] : [createNewSubformRow(sectionData, 0)];
            // Check if section has existing subform_rows (from props or initial data)
            const initialRows = (section && section.subform_rows && section.subform_rows.length > 0) 
                ? section.subform_rows 
                : subformRowsArr;
            
            const obj = {
                field_group_id: "",
                field_group_section_id: section.id,
                field_group_name: section.title || "",
                subform_rows: initialRows
            };
            
            hasAutoAddedRef.current = true;
            setSubFormDataAction(modType, [...isSet(subformData, []), obj]);
            setTimeout(() => {
                hasAutoAddedRef.current = false;
            }, 100);
        }
    }, [subformData, section, sectionData, modType, pageType, setSubFormDataAction]);
    
    // Calculate selected rows count for bulk actions
    const selectedRowsCount = currentSectionSubformData.subform_rows.filter((item) => item.is_selected === true).length;
    const canShowBulkActions = selectedRowsCount > 1;
    
    // Create all actions object - stored at section level under actions (e.g., "Package.actions.Add", "Package.actions.BulkEdit", "Package.actions.BulkDelete")
    
    // console.log('currentSectionSubformData',subformData);
    // console.log('elmtParams-11',elmtParams,currentSectionSubformData);
    // If children exist, use convertAndRenderElement for custom markup
    if (children && children.length > 0 && convertAndRenderElement) {
        // First, render any content outside the row loop (like Add button, Bulk actions)
        // Then render rows
        const rowElements = currentSectionSubformData.subform_rows.map((rowData, rowIndex) => {
            // Calculate button visibility for each row
            let isDeleteIconshow = false,
                isEditIconshow = false,
                isSaveIconshow = false,
                isCopyIconshow = false;

            if (pageType === "form" && isWrite === true) {
                isDeleteIconshow = true;
                isCopyIconshow = true;
                if (currentSectionSubformData.subform_rows.length > 1 && currentSectionSubformData.subform_rows.filter((val) => val.is_selected === true).length <= 1) {
                    isDeleteIconshow = true;
                } else {
                    isDeleteIconshow = false;
                }
                if (currentSectionSubformData.subform_rows.length >= 1 && currentSectionSubformData.subform_rows.filter((val) => val.is_selected === true).length <= 1) {
                    isCopyIconshow = true;
                } else {
                    isCopyIconshow = false;
                }
            } else if (pageType === "view" && isWrite === true) {
                if (rowData.isOpenBox === true) {
                    isDeleteIconshow = false;
                    isEditIconshow = false;
                    isSaveIconshow = true;
                } else if (currentSectionSubformData.subform_rows.length < 1) {
                    isDeleteIconshow = false;
                    isEditIconshow = true;
                    isSaveIconshow = false;
                } else {
                    isDeleteIconshow = true;
                    isEditIconshow = true;
                    isSaveIconshow = false;
                }
            }
            if (shouldRenderButton === false) {
                isCopyIconshow = shouldRenderButton;
            }

            // Get row handlers from factory function
            const rowHandlers = createRowHandlers(rowIndex);
            
            const buttonList = {
                edit: {
                    onClick: rowHandlers.editHandler,
                    type: "edit",
                    access_mode: isEditIconshow,
                },
                save: {
                    onClick: rowHandlers.saveHandler,
                    type: "save",
                    access_mode: isSaveIconshow,
                },
                cancel: {
                    onClick: rowHandlers.cancelHandler,
                    type: "cancel",
                    access_mode: isSaveIconshow,
                },
                clone: {
                    onClick: rowHandlers.copyHandler,
                    type: "copy",
                    access_mode: isCopyIconshow,
                },
                delete: {
                    onClick: rowHandlers.removeHandler,
                    type: "delete",
                    access_mode: isDeleteIconshow,
                },
            };
            // console.log('checkboxList',rowData);
            const checkboxList = {
                selected: {
                    checked:rowData?.is_selected || false,
                    selectedValue:rowData?.is_selected || false,
                    onChangeInputAction: (fieldObj,e) => {
                        // Use the selectHandler matching pattern from index.js
                        // console.log('fieldObj',fieldObj,e.target.checked);
                        selectDataHandler(rowIndex, e.target.checked);
                    },
                    withoutBlock:true,
                    is_subform_field:true,
                    id:`addForm_${rowIndex}`,
                    name:`addForm_${rowIndex}`,
                    type:"CheckBoxes",
                    element:"CheckBoxes",
                    access_mode: pageType === "form" && currentSectionSubformData.subform_rows.length > 1
                },
                selectAll: {
                    type:"CheckBoxes",
                    element:"CheckBoxes",
                    is_subform_field:true,
                    checked: currentSectionSubformData.subform_rows.every(row => row.is_selected === true),
                    onChangeInputAction: selectAllHandler,
                    access_mode: pageType === "form" && currentSectionSubformData.subform_rows.length > 1
                }
            };
            
            // Process all fields for this row (from ScmSubFormItem logic)
            const processedFields = sectionData
                .filter((item) => 
                    isSet(item.access_mode, "EDITABLE") !== "HIDDEN" &&
                    isSet(item.is_add_form, false) !== false
                )
                .map((item, index) => {
                    return {
                        index,
                        item,
                        ...processFieldDataWrapper(item, rowData, rowIndex, rowData.id)
                    };
                });
            
            // Create updated dynamicVar with loop variable set (e.g., "pkg" from datatable var attribute)
            // This allows ScmInputTextField to access "pkg.Length" etc.
            let updatedDynamicVar = { ...elmtParams.dynamicVar };
            
            // Note: Section-level actions are already set up in ScmInputSectionField
            // They access subform data from defProps when called, so no update needed here
            // console.log('updatedDynamicVar',updatedDynamicVar);
            
            // Get the loop variable name from elmtParams (passed from ScmDatatableField)
            // const loopVariableName = elmtParams.loopVariableName || 'pkg';
            
            // Set each field in dynamicVar so "pkg.Length" works
            // When name="pkg.Length", pkg should be the field object for "Length"
            // if (section && section.data) {
            //     section.data.forEach((fieldItem) => {
            //         const fieldLabelName = fieldItem.label?.blocks?.[0]?.text || '';
            //         if (fieldLabelName) {
            //             // Set the field as a variable in dynamicVar by its label name (e.g., "length")
            //             updatedDynamicVar[fieldLabelName.toLowerCase()] = {
            //                 ...fieldItem,
            //                 is_subform_field: true
            //             };
                        
            //             // Also set the loop variable (e.g., "pkg") to point to this field object
            //             // This way "pkg.Length" will work where pkg is the Length field object
            //             updatedDynamicVar[loopVariableName] = {
            //                 ...fieldItem,
            //                 is_subform_field: true
            //             };
            //         }
            //     });
            // }
            updatedDynamicVar={
                ...updatedDynamicVar,
                [attributes.var]:{
                    ...updatedDynamicVar[attributes.entries],
                    loopIndex:rowIndex,
                    actions:buttonList,
                    checkbox:checkboxList

                },
            }
            // Set all ScmSubFormBody methods and variables in dynamicVar under subformvar key
            updatedDynamicVar.subformvar = {
                // Handler methods
                removeSubFormsHandler: rowHandlers.removeHandler,
                cancelSubFormsHandler: rowHandlers.cancelHandler,
                editSubFormsHandler: rowHandlers.editHandler,
                saveSubFormsHandler: rowHandlers.saveHandler,
                copySubFormsHandler: rowHandlers.copyHandler,
                addSubformRowHandler: addSubformRowHandler,
                setSubFormDataAction: setSubFormDataAction,
                clearSubformValidation: clearSubformValidation,
                onChangeInputAction: onChangeInputAction,
                processFieldData: (item) => processFieldDataWrapper(item, rowData, rowIndex, rowData.id),
                selectHandler: finalSelectHandler ? (val) => finalSelectHandler(rowIndex, val) : null,
                selectAllHandler: selectAllHandler,
                
                // Variables
                sectionSubformData: currentSectionSubformData,
                subformIndex: subFormIndex,
                sectionData: sectionData,
                colCount: colCount,
                modType: modType,
                isWrite: isWrite,
                pageType: pageType,
                section: section,
                reminderDisplayText: reminderDisplayText,
                shouldRenderButton: shouldRenderButton,
                rowData: rowData,
                rowIndex: rowIndex,
                processedFields: processedFields,
                buttonList: buttonList,
                isOpenBox: rowData.isOpenBox,
                subformData: subformData,
                isSelected: rowData.is_selected || false,
                isSelectAll: currentSectionSubformData.subform_rows.every(row => row.is_selected === true),
                
                // Button visibility flags
                isDeleteIconshow: isDeleteIconshow,
                isEditIconshow: isEditIconshow,
                isSaveIconshow: isSaveIconshow,
                isCopyIconshow: isCopyIconshow
            };
            // console.log('updatedDynamicVar',updatedDynamicVar);
            return convertAndRenderElement(children, {
                ...elmtParams,
                dynamicVar: updatedDynamicVar,
                defProps: {
                    ...defProps,
                    // Ensure layout rules are passed to children for re-evaluation
                    layoutRules: layoutRules,
                    rulesMapping: rulesMapping,
                    layoutObject: layoutObject,
                    fieldGroups: fieldGroups,
                    validationForms: validationForms
                }
            });
        });
        
        // Wrap row elements in a fragment or return as array
        return rowElements;
    }
    
};

const mapDispatchToProps = (dispatch) => {
    return {
        setSubFormDataAction: (modType, data) =>
            dispatch(setSubFormDataAction(modType, data)),
        clearSubformValidation: (modType, row) =>
            dispatch(clearSubformValidation(modType, row)),
    };
};

ScmSubFormBody.defaultProps = {
    shouldRenderButton: true
};

export default connect(null, mapDispatchToProps)(React.memo(ScmSubFormBody));

